const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface RequestOptions {
  params?: Record<string, string | number>;
}

// Utility function to check if JWT token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (!exp) return false;
    // Check if token is expired (with 5 second buffer)
    return Date.now() >= exp * 1000 - 5000;
  } catch {
    return true; // If we can't parse, consider it expired
  }
};

// Logout callback that will be set by AuthContext
let logoutCallback: (() => void) | null = null;

// Function to set logout callback
export const setLogoutCallback = (callback: () => void) => {
  logoutCallback = callback;
};

// Helper function to get headers with token
const getHeaders = (includeAuth: boolean = true): HeadersInit => {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token is expired before making request
      if (isTokenExpired(token)) {
        if (logoutCallback) {
          logoutCallback();
        }
        throw new Error('Token expired');
      }
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Helper function to handle response errors
const handleResponse = async (response: Response): Promise<void> => {
  if (response.status === 401 || response.status === 403) {
    // Token expired or unauthorized
    if (logoutCallback && !response.url.includes('/auth/login')) {
      logoutCallback();
    }

    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: 'Session expired. Please login again.' };
    }

    const errorMessage =
      errorData?.error || errorData?.message || 'Session expired. Please login again.';
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: `HTTP error! status: ${response.status}` };
    }
    const errorMessage =
      errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).response = { data: errorData, status: response.status };
    throw error;
  }
};

const apiClient = {
  async get<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    try {
      let url = `${API_BASE_URL}${endpoint}`;

      if (options.params) {
        const params = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          params.append(key, String(value));
        });
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: getHeaders(),
      });

      await handleResponse(response);
      return response.json();
    } catch (error) {
      console.error('API GET Error:', error);
      throw error;
    }
  },

  async post<T = any>(endpoint: string, data: any, skipAuth: boolean = false): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: getHeaders(!skipAuth),
        body: JSON.stringify(data),
      });

      await handleResponse(response);
      return response.json();
    } catch (error) {
      console.error('API POST Error:', error);
      throw error;
    }
  },

  async put<T = any>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      await handleResponse(response);
      return response.json();
    } catch (error) {
      console.error('API PUT Error:', error);
      throw error;
    }
  },

  async delete<T = any>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      await handleResponse(response);
      return response.json();
    } catch (error) {
      console.error('API DELETE Error:', error);
      throw error;
    }
  },

  async patch<T = any>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      await handleResponse(response);
      return response.json();
    } catch (error) {
      console.error('API PATCH Error:', error);
      throw error;
    }
  },

  async upload<T = any>(endpoint: string, formData: FormData): Promise<T> {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {};

      if (token) {
        if (isTokenExpired(token)) {
          if (logoutCallback) {
            logoutCallback();
          }
          throw new Error('Token expired');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      await handleResponse(response);
      return response.json();
    } catch (error) {
      console.error('API Upload Error:', error);
      throw error;
    }
  },
};

export default apiClient;
